import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getSpans, type Span, type GetSpansParams } from "../api/client";
import type { ServiceSlug } from "../api/client";
import type { TimeRange } from "../utils/time";

export interface UseSpansOptions {
  serviceSlug: ServiceSlug | null;
  timeRange: TimeRange;
  serviceNames?: string[];
  serviceInstances?: string[];
  spanNames?: string[];
  traceIds?: string[];
  spanIds?: string[];
  minDurationMs?: number | null;
  maxDurationMs?: number | null;
  isRoot?: boolean | null;
  sort?: string;
  order?: string;
  enabled?: boolean;
  limit?: number;
  refreshToken?: number;
}

export interface UseSpansResult {
  spans: Span[];
  loading: boolean;
  isFetching: boolean;
  error: string | null;
}

export function useSpans({
  serviceSlug,
  timeRange,
  serviceNames = [],
  serviceInstances = [],
  spanNames = [],
  traceIds = [],
  spanIds = [],
  minDurationMs = null,
  maxDurationMs = null,
  isRoot = null,
  sort,
  order,
  enabled = true,
  limit,
  refreshToken = 0,
}: UseSpansOptions): UseSpansResult {
  const traceIdsKey = traceIds.join(",");
  const spanIdsKey = spanIds.join(",");
  const serviceNamesKey = serviceNames.join(",");
  const serviceInstancesKey = serviceInstances.join(",");
  const spanNamesKey = spanNames.join(",");

  const query = useQuery({
    queryKey: [
      "spans",
      serviceSlug,
      timeRange.from.toISOString(),
      timeRange.to.toISOString(),
      serviceNamesKey,
      serviceInstancesKey,
      spanNamesKey,
      traceIdsKey,
      spanIdsKey,
      minDurationMs ?? null,
      maxDurationMs ?? null,
      isRoot ?? null,
      sort ?? null,
      order ?? null,
      limit ?? null,
      refreshToken,
    ],
    enabled: !!serviceSlug && enabled,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => {
      const params: GetSpansParams = {
        serviceSlug: serviceSlug!,
        startTime: timeRange.from,
        endTime: timeRange.to,
        serviceNames,
        serviceInstances,
        spanNames,
        traceIds,
        spanIds,
        minDurationMs: minDurationMs ?? undefined,
        maxDurationMs: maxDurationMs ?? undefined,
        isRoot: isRoot ?? undefined,
        sort,
        order,
        limit,
        signal,
      };
      return getSpans(params);
    },
  });

  if (!serviceSlug || !enabled) {
    return { spans: [], loading: false, isFetching: false, error: null };
  }

  const error =
    query.error instanceof Error
      ? query.error.message
      : query.error
        ? "Failed to load spans"
        : null;
  return {
    spans: query.data ?? [],
    loading: query.isLoading,
    isFetching: query.isFetching,
    error,
  };
}
